import {useEffect, useMemo, useRef, useState} from 'react';
import {useShopping} from '../contexts/ShoppingContext';
import {useLocation, useNavigate} from 'react-router-dom';
import {ActivePromosModal} from './ActivePromosModal';
import {ActiveGiftClaimsModal} from './ActiveGiftClaimsModal';
import {useClientDataContext} from '../contexts/ClientDataContext';
import {useGuestMode} from '../hooks/useGuestMode';
import {DepartmentService} from '../services/departmentService';
import {ClientMessageBanner} from './header/ClientMessageBanner';
import {HeaderLogo} from './header/HeaderLogo';
import {HeaderActions} from './header/HeaderActions';
import {MobileMenu} from './header/MobileMenu';
import {DepartmentNav} from './header/DepartmentNav';
import {Menu, Search, X} from 'lucide-react';

export function Header()  {
    const {state, dispatch, productsLoading} = useShopping();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showPromosModal, setShowPromosModal] = useState(false);
    const [showGiftClaimsModal, setShowGiftClaimsModal] = useState(false);
    const [isBannerExpanded, setIsBannerExpanded] = useState(true);
    const {clientData, loading: clientLoading} = useClientDataContext();
    const {isGuest} = useGuestMode();
    const navigate = useNavigate();
    const location = useLocation();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(() => !!new URLSearchParams(window.location.search).get('search'));
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Header height tracking
    const headerRef = useRef<HTMLElement>(null);
    const mobileSearchRef = useRef<HTMLInputElement>(null);

    const cartItemsCount = state.cart.reduce((total, item) => total + item.quantity, 0);

    // Get URL parameters
    const urlParams = new URLSearchParams(location.search);
    const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : null;
    const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : null;
    const currentSearch = urlParams.get('search') || '';

    // Focus mobile search input when opened
    const handleMobileSearchOpen = () => {
        setIsMenuOpen(false);
        // Focus synchronously within user gesture — iOS requires this to show keyboard
        mobileSearchRef.current?.focus();
        setIsSearchOpen(true);
    };

    // Sync search query with URL on mount and location change
    const prevSearchRef = useRef(currentSearch);
    useEffect(() => {
        const prev = prevSearchRef.current;
        prevSearchRef.current = currentSearch;
        setSearchQuery(currentSearch);
        // Search cleared externally (e.g. logo click) — reload all departments and close search bar
        // On mobile, don't auto-close (user clears to retype, explicit X button closes)
        if (prev && !currentSearch) {
            DepartmentService.getDepartments()
                .then(departments => {
                    window.dispatchEvent(new CustomEvent('departmentsUpdated', { detail: departments }));
                })
                .catch(() => {});
        }
    }, [currentSearch]);

    // Debounce search query and update URL
    useEffect(() => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Don't trigger on initial mount if search is empty
        if (searchQuery === '' && currentSearch === '') {
            return;
        }

        // Don't trigger if searchQuery is already in sync with URL (e.g., on page refresh)
        if (searchQuery.trim() === currentSearch.trim()) {
            return;
        }

        const debounceMs = window.innerWidth < 768 ? 800 : 500;
        searchTimeoutRef.current = setTimeout(async () => {
            const searchParams = new URLSearchParams(location.search);

            if (searchQuery.trim()) {
                // When searching, update departments with search filter
                try {
                    const departments = await DepartmentService.getDepartments(searchQuery.trim());
                    // Notify UI to update departments
                    if (typeof window !== 'undefined') {
                        const event = new CustomEvent('departmentsUpdated', {detail: departments});
                        window.dispatchEvent(event);
                    }
                } catch (error) {
                    console.warn('Failed to update departments for search:', error);
                }

                // Clear filters and set search parameter (reset page to 1)
                searchParams.delete('departmentId');
                searchParams.delete('categoryId');
                searchParams.delete('page');
                searchParams.set('search', searchQuery.trim());
            } else {
                // When clearing search, reload all departments
                try {
                    const departments = await DepartmentService.getDepartments();
                    // Notify UI to update departments
                    if (typeof window !== 'undefined') {
                        const event = new CustomEvent('departmentsUpdated', {detail: departments});
                        window.dispatchEvent(event);
                    }
                } catch (error) {
                    console.warn('Failed to reload departments after clearing search:', error);
                }

                // Remove search parameter (keep filters, reset page)
                searchParams.delete('search');
                searchParams.delete('page');
            }

            navigate(searchParams.toString() ? `/?${searchParams.toString()}` : '/');
        }, debounceMs);

        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, currentSearch, navigate, location.search]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    // Get departments from state
    const departments = state.departments;

    // Get categories filtered by selected department (for mobile menu)
    const categories = useMemo(() => {
        if (!departments) return [];

        if (!selectedDepartmentId) {
            // No department selected - show all categories, deduplicated by ID
            const allCategories = departments.flatMap(dept => dept.productCategories || []);
            return allCategories.filter((category, index, self) =>
                index === self.findIndex(c => c.id === category.id)
            );
        }

        // Department selected - find matching department and return its categories
        const matchedDepartment = departments.find(dept =>
            Number(dept.id) === Number(selectedDepartmentId)
        );

        return matchedDepartment?.productCategories || [];
    }, [departments, selectedDepartmentId]);

    const handleDepartmentClick = (departmentId: number | null) => {
        const searchParams = new URLSearchParams();
        if (currentSearch) searchParams.set('search', currentSearch);
        if (departmentId !== null) {
            searchParams.set('departmentId', departmentId.toString());
            // Auto-select if only one category
            const dept = departments.find(d => Number(d.id) === departmentId);
            if (dept?.productCategories?.length === 1) {
                searchParams.set('categoryId', dept.productCategories[0].id.toString());
            }
        }
        navigate(searchParams.toString() ? `/?${searchParams.toString()}` : '/');
        setIsMenuOpen(false);
    };

    const handleCategoryClick = (categoryId: number, departmentId?: number) => {
        const searchParams = new URLSearchParams(location.search);
        if (departmentId !== undefined) {
            searchParams.set('departmentId', departmentId.toString());
        }
        searchParams.set('categoryId', categoryId.toString());
        searchParams.delete('page');
        navigate(`/?${searchParams.toString()}`);
        setIsMenuOpen(false);
    };

    // Update header height CSS variable for CategoryNav positioning
    useEffect(() => {
        const header = headerRef.current;
        if (!header) return;
        const update = () => document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    const themeColors = clientData?.themeColors;
    const safeHeaderText = themeColors?.left_side_color || '#e5e7eb';

    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hoverOpenedAtRef = useRef<number | null>(null);
    const hoverCooldownRef = useRef(false);
    const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);

    // If cart was opened by hover and closed within 2s → block hover for 10s
    const isCartOpen = state.isCartOpen;
    useEffect(() => {
        if (!isCartOpen && hoverOpenedAtRef.current !== null) {
            const openDuration = Date.now() - hoverOpenedAtRef.current;
            hoverOpenedAtRef.current = null;
            if (openDuration < 2000) {
                hoverCooldownRef.current = true;
                if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
                cooldownTimerRef.current = setTimeout(() => {
                    hoverCooldownRef.current = false;
                }, 10000);
            }
        }
    }, [isCartOpen]);

    const handleCartClick = () => {
        setIsMenuOpen(false);
        dispatch({type: 'SET_CART_OPEN', payload: true});
    };

    const handleCartMouseEnter = () => {
        if (hoverCooldownRef.current) return;
        hoverTimeoutRef.current = setTimeout(() => {
            hoverOpenedAtRef.current = Date.now();
            dispatch({type: 'SET_CART_OPEN', payload: true});
        }, 500);
    };

    const handleCartMouseLeave = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };

    if (clientLoading || !themeColors) return (
        <header
            ref={headerRef}
            className="shadow-md fixed top-0 left-0 right-0 z-40 bg-gray-900"
            role="banner"
            aria-busy="true"
            aria-label="Loading header"
        >
            <div className="px-4 sm:px-6 lg:px-8">
                <div className="flex items-center h-16 gap-6 animate-pulse">
                    {/* Hamburger — mobile only */}
                    <div className="md:hidden w-9 h-9 bg-gray-700 rounded-md flex-shrink-0" />
                    {/* Logo */}
                    <div className="w-28 h-8 bg-gray-700 rounded flex-shrink-0" />
                    {/* Department nav — desktop only */}
                    <div className="flex-1 hidden md:flex items-center gap-6">
                        {[100, 80, 110, 90, 120, 85].map((w, i) => (
                            <div key={i} className="h-3 bg-gray-700 rounded flex-shrink-0" style={{ width: w }} />
                        ))}
                    </div>
                    {/* Right actions */}
                    <div className="ml-auto flex items-center gap-2">
                        {/* Promo pill — desktop */}
                        <div className="hidden sm:flex items-center gap-1.5 px-2.5 h-7 bg-gray-700 rounded-full w-20" />
                        <div className="hidden sm:block w-px h-5 bg-gray-600 mx-1" />
                        {/* Search icon */}
                        <div className="w-9 h-9 bg-gray-700 rounded-md flex-shrink-0" />
                        {/* Cart icon */}
                        <div className="w-9 h-9 bg-gray-700 rounded-md flex-shrink-0" />
                        {/* User initials — circular */}
                        <div className="w-8 h-8 bg-gray-700 rounded-full flex-shrink-0" />
                    </div>
                </div>
            </div>
        </header>
    );

    return (
        <header
            ref={headerRef}
            className="shadow-md fixed top-0 left-0 right-0 z-40"
            style={{backgroundColor: themeColors?.header_background}}
            role="banner"
        >
            {/* Client Message Banner or Welcome Popup */}
            {(() => {
                const message = clientData?.store?.isExpired
                    ? clientData?.storeMessages?.saleExpired
                    : clientData?.storeMessages?.welcome;
                return message ? (
                    <ClientMessageBanner
                        message={message}
                        isExpanded={isBannerExpanded}
                        onToggle={() => setIsBannerExpanded(!isBannerExpanded)}
                    />
                ) : null;
            })()}

            {/* Store Timing Banner (guests only) */}
            {isGuest && clientData?.store && (() => {
                const store = clientData.store!;
                let label: string | null = null;

                if (store.isOpen && store.closeDate) {
                    label = `Store closes on ${new Date(store.closeDate).toLocaleString()}`;
                } else if (store.isUpcoming && store.openDate) {
                    label = `Store opens on ${new Date(store.openDate).toLocaleString()}`;
                }

                return label ? (
                    <div className="text-center text-sm py-1.5 px-4 font-medium"
                         style={{
                             backgroundColor: store.isOpen ? '#fef9c3' : '#fee2e2',
                             color: store.isOpen ? '#854d0e' : '#991b1b',
                         }}>
                        {label}
                    </div>
                ) : null;
            })()}

            {/* Main Header — single row */}
            <div className="px-4 sm:px-6 lg:px-8">
                <div className="flex items-center h-16 gap-6">

                    {/* Mobile: hamburger on the left */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden p-2 rounded-md transition-colors focus:outline-none hover:bg-white/10"
                        style={{ color: themeColors?.header_cart_background || '#9ca3af' }}
                        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>

                    {/* Logo */}
                    <div className="md:flex-shrink-0" onClick={() => setIsMenuOpen(false)}>
                        {clientData?.logoUrl && (
                            <HeaderLogo logoUrl={clientData.logoUrl} />
                        )}
                    </div>

                    {/* Inline department nav — desktop only */}
                    <div className="flex-1 min-w-0 hidden md:flex h-full">
                        <DepartmentNav
                            departments={departments}
                            selectedDepartmentId={selectedDepartmentId}
                            onDepartmentClick={handleDepartmentClick}
                            themeColors={themeColors}
                            inline
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 ml-auto md:ml-0">
                        <HeaderActions
                            isSearchOpen={isSearchOpen}
                            onSearchOpen={() => setIsSearchOpen(true)}
                            onMobileSearchOpen={handleMobileSearchOpen}
                            onSearchClose={() => setIsSearchOpen(false)}
                            searchQuery={searchQuery}
                            onSearchChange={handleSearchChange}
                            searchLoading={isSearchOpen && searchQuery.trim() !== currentSearch.trim()}
                            productsLoading={productsLoading}
                            cartItemsCount={cartItemsCount}
                            onCartClick={handleCartClick}
                            onCartMouseEnter={handleCartMouseEnter}
                            onCartMouseLeave={handleCartMouseLeave}
                            onShowPromos={() => setShowPromosModal(true)}
                            onShowGiftClaims={() => setShowGiftClaimsModal(true)}
                            isMenuOpen={isMenuOpen}
                            onToggleMenu={() => setIsMenuOpen(!isMenuOpen)}
                            themeColors={themeColors}
                            showCart={location.pathname !== '/checkout'}
                        />
                    </div>
                </div>
            </div>

            {/* Mobile search slide-down panel */}
            <div
                className="md:hidden overflow-clip"
                style={{
                    maxHeight: isSearchOpen ? '72px' : '0',
                    transition: 'max-height 320ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <div
                    className="px-3 py-2.5"
                    style={{
                        borderTop: `1px solid color-mix(in srgb, ${safeHeaderText} 15%, transparent)`,
                        borderBottom: `1px solid color-mix(in srgb, ${safeHeaderText} 15%, transparent)`,
                        backgroundColor: themeColors?.header_background,
                    }}
                >
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none opacity-40"
                            style={{ color: themeColors?.header_cart_background || '#9ca3af' }} aria-hidden="true" />
                        <input
                            ref={mobileSearchRef}
                            type="search"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={e => e.key === 'Escape' && setIsSearchOpen(false)}
                            onFocus={() => {
                                // iOS: lock window scroll while keyboard is open so there's
                                // no bounce-back animation when keyboard closes
                                document.body.style.overflow = 'hidden';
                            }}
                            onBlur={() => {
                                // Release window scroll lock immediately — before iOS can
                                // start its viewport-restore animation
                                document.body.style.overflow = '';
                                // Snap window to top in case iOS scrolled it to show the input
                                window.scrollTo(0, 0);
                            }}
                            placeholder="Search products..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:outline-none [&::-webkit-search-cancel-button]:hidden [&::placeholder]:opacity-40"
                            style={{
                                fontSize: '16px',
                                backgroundColor: `color-mix(in srgb, ${safeHeaderText} 6%, transparent)`,
                                color: safeHeaderText,
                                border: `1px solid color-mix(in srgb, ${safeHeaderText} 30%, transparent)`,
                            }}
                            aria-label="Search products"
                        />
                    </div>
                </div>
            </div>

            {/* Mobile Navigation — full-screen overlay, always in DOM for exit animation */}
            <MobileMenu
                isOpen={isMenuOpen}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                departments={departments}
                categories={categories}
                selectedDepartmentId={selectedDepartmentId}
                selectedCategoryId={selectedCategoryId}
                onDepartmentClick={handleDepartmentClick}
                onCategoryClick={handleCategoryClick}
                onAllProductsClick={() => handleDepartmentClick(null)}
                themeColors={themeColors}
            />

            {/* Modals */}
            <ActivePromosModal
                isOpen={showPromosModal}
                onClose={() => setShowPromosModal(false)}
            />
            <ActiveGiftClaimsModal
                isOpen={showGiftClaimsModal}
                onClose={() => setShowGiftClaimsModal(false)}
            />
        </header>
    );
}
